import type { ReactNode } from 'react'
import type { SectorRef, StockRow } from '@/lib/market/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { squarify } from '@/components/charts/squarify'
import { useElementWidth } from '@/hooks/use-element-width'
import { useQuotes } from '@/components/market/live-provider'
import { Segmented } from '@/components/market/segmented'
import { formatKes, formatPercent, formatPrice } from '@/lib/market/format'

type MapPeriod = 'd1' | 'w1' | 'm1' | 'ytd'

interface MarketMapProps {
    stocks: StockRow[]
    sectors: SectorRef[]
    height?: number
}

const PERIODS: Array<{ value: MapPeriod, label: string }> = [
    { value: 'd1', label: 'Today' },
    { value: 'w1', label: '1W' },
    { value: 'm1', label: '1M' },
    { value: 'ytd', label: 'YTD' }
]

const SCALE: Record<MapPeriod, number> = { d1: 3, w1: 6, m1: 10, ytd: 25 }

export const divergingFill = (value: number, limit: number): { background: string, ink: string } => {
    const strength = Math.min(1, Math.abs(value) / limit)
    const percent = Math.round(strength * 100)

    if (Math.abs(value) < 0.005) return { background: 'var(--diverging-mid)', ink: '#f5f7fa' }

    const color = value > 0 ? 'var(--up)' : 'var(--down)'

    return {
        background: `color-mix(in oklab, ${color} ${percent}%, var(--diverging-mid))`,
        ink: strength >= 0.65 ? '#0a0f14' : '#f5f7fa'
    }
}

const MarketMap = ({ stocks, sectors, height = 460 }: MarketMapProps): ReactNode => {
    const [ref, width] = useElementWidth<HTMLDivElement>(960)
    const [period, setPeriod] = useState<MapPeriod>('d1')
    const [hovered, setHovered] = useState<string | null>(null)
    const specs = useMemo(() => stocks.map(stock => stock.live), [stocks])
    const quotes = useQuotes(specs)
    const mapHeight = width < 640 ? 380 : height

    const values = useMemo(() => stocks.map((stock, index) => {
        if (period === 'd1') return quotes[index].changePct

        return stock.perf[period] ?? 0
    }), [stocks, quotes, period])

    const layout = useMemo(() => {
        const caps = stocks.map(stock => stock.live.prevClose * stock.shares)
        const groups = new Map<string, number[]>()

        stocks.forEach((stock, index) => {
            const key = stock.sectorSlug ?? 'unclassified'
            groups.set(key, [...(groups.get(key) ?? []), index])
        })

        const sectorRects = squarify(
            Array.from(groups.entries()).map(([key, members]) => ({ key, value: members.reduce((acc, member) => acc + caps[member], 0) })),
            0,
            0,
            width,
            mapHeight
        )

        return sectorRects.map(rect => {
            const members = groups.get(rect.key) ?? []
            const header = rect.w > 110 && rect.h > 70 ? 20 : 0
            const tiles = squarify(
                members.map(member => ({ key: String(member), value: caps[member] })),
                rect.x + 1,
                rect.y + header + 1,
                Math.max(0, rect.w - 2),
                Math.max(0, rect.h - header - 2)
            )

            return { rect, header, tiles }
        })
    }, [stocks, width, mapHeight])

    const sectorLookup = useMemo(() => new Map(sectors.map(sector => [sector.slug, sector])), [sectors])
    const hoveredIndex = hovered === null ? -1 : Number(hovered)
    const hoveredStock = hoveredIndex >= 0 ? stocks[hoveredIndex] : null

    return (
        <div>
            <div className='mb-3 flex flex-wrap items-center justify-between gap-3'>
                <Segmented label='Market map period' options={PERIODS} value={period} onChange={setPeriod} />

                <div className='flex items-center gap-2 text-[11px] text-muted-foreground' aria-hidden='true'>
                    <span>{formatPercent(-SCALE[period], 0)}</span>
                    <span className='h-2 w-28 rounded-full' style={{ background: 'linear-gradient(90deg, var(--down), var(--diverging-mid), var(--up))' }} />
                    <span>{formatPercent(SCALE[period], 0)}</span>
                </div>
            </div>

            <div
                ref={ref}
                className='relative w-full overflow-hidden rounded-lg bg-background'
                style={{ height: mapHeight }}
                onMouseLeave={() => setHovered(null)}
            >
                {layout.map(({ rect, header, tiles }) => {
                    const sector = sectorLookup.get(rect.key)

                    return (
                        <div key={rect.key}>
                            {header > 0 && (
                                <div
                                    className='absolute truncate px-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground'
                                    style={{ left: rect.x, top: rect.y + 3, width: rect.w }}
                                >
                                    {sector
                                        ? <span data-mdlfy-cms={`sectors:${sector.id}:name`}>{sector.name}</span>
                                        : 'Unclassified'}
                                </div>
                            )}

                            {tiles.map(tile => {
                                const index = Number(tile.key)
                                const stock = stocks[index]
                                const value = values[index]
                                const fill = divergingFill(value, SCALE[period])
                                const showTicker = tile.w > 34 && tile.h > 22
                                const showValue = tile.w > 52 && tile.h > 38
                                const showPrice = tile.w > 90 && tile.h > 70
                                const big = tile.w > 150 && tile.h > 110

                                return (
                                    <Link
                                        key={stock.id}
                                        href={`/stocks/${stock.slug}`}
                                        aria-label={`${stock.ticker}, ${formatPercent(value)}`}
                                        onMouseEnter={() => setHovered(tile.key)}
                                        onFocus={() => setHovered(tile.key)}
                                        className={cn(
                                            'absolute flex flex-col items-center justify-center overflow-hidden rounded-[3px] text-center transition-[filter] hover:brightness-125 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-foreground',
                                            hoveredIndex === index && 'brightness-125'
                                        )}
                                        style={{
                                            left: tile.x + 1,
                                            top: tile.y + 1,
                                            width: Math.max(0, tile.w - 2),
                                            height: Math.max(0, tile.h - 2),
                                            background: fill.background,
                                            color: fill.ink
                                        }}
                                    >
                                        {showTicker && (
                                            <span className={cn('font-mono font-semibold leading-tight tracking-tight', big ? 'text-lg' : 'text-[11px]')}>
                                                {stock.ticker}
                                            </span>
                                        )}
                                        {showValue && (
                                            <span className={cn('tabular-nums leading-tight opacity-90', big ? 'text-sm' : 'text-[10.5px]')}>
                                                {formatPercent(value)}
                                            </span>
                                        )}
                                        {showPrice && (
                                            <span className='mt-0.5 text-[10.5px] tabular-nums opacity-75'>
                                                {formatPrice(quotes[index].last)}
                                            </span>
                                        )}
                                    </Link>
                                )
                            })}
                        </div>
                    )
                })}
            </div>

            <div className='mt-3 min-h-10 rounded-lg border border-border bg-background/60 px-3 py-2 text-xs' aria-live='polite'>
                {hoveredStock ? (
                    <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                        <span className='font-mono font-semibold text-primary'>{hoveredStock.ticker}</span>
                        <span className='font-medium text-foreground' data-mdlfy-cms={`companies:${hoveredStock.id}:name`}>{hoveredStock.name}</span>
                        <span className='tabular-nums text-muted-foreground'>KES {formatPrice(quotes[hoveredIndex].last)}</span>
                        <span className={cn('tabular-nums', values[hoveredIndex] > 0 ? 'text-up' : values[hoveredIndex] < 0 ? 'text-down' : 'text-muted-foreground')}>
                            {formatPercent(values[hoveredIndex])} {PERIODS.find(item => item.value === period)?.label.toLowerCase()}
                        </span>
                        <span className='tabular-nums text-muted-foreground'>Market cap {formatKes(quotes[hoveredIndex].last * hoveredStock.shares)}</span>
                    </div>
                ) : (
                    <p className='text-muted-foreground'>Tiles are sized by market value and coloured by price change. Hover or focus a tile for details, select it to open the company.</p>
                )}
            </div>
        </div>
    )
}

export default MarketMap
